'use client';

import React from 'react';
import type { CustomBackground } from '@/hooks/useCustomBackgroundImage';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Mic, Video, ImageIcon } from 'lucide-react';
import { PreJoinSettingsMenu } from '@/components/PreJoinSettingsMenu';

interface MobileSettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  view: 'menu' | 'audio' | 'video' | 'backgrounds';
  onViewChange: (view: 'menu' | 'audio' | 'video' | 'backgrounds') => void;
  audioEnabled: boolean;
  setAudioEnabled: (enabled: boolean) => void;
  videoEnabled: boolean;
  setVideoEnabled: (enabled: boolean) => void;
  audioDeviceId: string;
  setAudioDeviceId: (deviceId: string) => void;
  videoDeviceId: string;
  setVideoDeviceId: (deviceId: string) => void;
  audioInputDevices: MediaDeviceInfo[];
  videoInputDevices: MediaDeviceInfo[];
  audioOutputDevices: MediaDeviceInfo[];
  audioOutputDeviceId: string;
  setAudioOutputDeviceId: (deviceId: string) => void;
  videoTrack?: any;
  backgroundType: 'none' | 'blur' | 'image';
  virtualBackgroundImagePath: string;
  selectBackground: (type: 'none' | 'blur' | 'image', imagePath?: string) => void;
  customBackgrounds: CustomBackground[];
  uploadCustomBackground: (
    file: File,
  ) => Promise<{ success: boolean; message?: string; dataUrl?: string }>;
  removeCustomBackground: (id: string) => void;
  isUploadingCustomBackground: boolean;
  customBackgroundError: string | null;
  resetCustomBackgroundError: () => void;
  audioLevel: number;
  isAnalyzingAudio: boolean;
  isPlayingTestSound: boolean;
  playTestSound: () => void;
  supportsSetSinkId: boolean;
}

export function MobileSettingsModal({
  open,
  onOpenChange,
  view,
  onViewChange,
  audioEnabled,
  setAudioEnabled,
  videoEnabled,
  setVideoEnabled,
  audioDeviceId,
  setAudioDeviceId,
  videoDeviceId,
  setVideoDeviceId,
  audioInputDevices,
  videoInputDevices,
  audioOutputDevices,
  audioOutputDeviceId,
  setAudioOutputDeviceId,
  videoTrack,
  backgroundType,
  virtualBackgroundImagePath,
  selectBackground,
  customBackgrounds,
  uploadCustomBackground,
  removeCustomBackground,
  isUploadingCustomBackground,
  customBackgroundError,
  resetCustomBackgroundError,
  audioLevel,
  isAnalyzingAudio,
  isPlayingTestSound,
  playTestSound,
  supportsSetSinkId,
}: MobileSettingsModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="md:hidden h-screen w-screen max-w-none max-h-none rounded-none p-0 bg-neutral-900 border-0"
        showCloseButton={false}
      >
        <DialogTitle className="sr-only">Configurações</DialogTitle>
        <div className="h-full flex flex-col">
          {/* Header do modal mobile */}
          <div className="flex items-center justify-between p-4 border-b border-neutral-700">
            <div className="flex items-center gap-3">
              {view !== 'menu' && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-neutral-800"
                  onClick={() => onViewChange('menu')}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Voltar
                </Button>
              )}
              <h2 className="text-lg font-semibold text-white">
                {view === 'menu' && 'Configurações'}
                {view === 'audio' && 'Áudio'}
                {view === 'video' && 'Vídeo'}
                {view === 'backgrounds' && 'Fundos Virtuais'}
              </h2>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-white hover:bg-neutral-800"
              onClick={() => onOpenChange(false)}
            >
              ✕
            </Button>
          </div>

          {/* Conteúdo do modal mobile */}
          <div className="flex-1 overflow-y-auto p-4">
            {view === 'menu' && (
              <div className="space-y-4">
                <button
                  className="w-full p-4 bg-neutral-800 rounded-lg text-left text-white hover:bg-neutral-700 transition-colors"
                  onClick={() => onViewChange('audio')}
                >
                  <div className="flex items-center gap-3">
                    <Mic className="w-6 h-6" />
                    <div>
                      <div className="font-medium">Áudio</div>
                      <div className="text-sm text-neutral-400">
                        Microfone e dispositivos de áudio
                      </div>
                    </div>
                  </div>
                </button>

                <button
                  className="w-full p-4 bg-neutral-800 rounded-lg text-left text-white hover:bg-neutral-700 transition-colors"
                  onClick={() => onViewChange('video')}
                >
                  <div className="flex items-center gap-3">
                    <Video className="w-6 h-6" />
                    <div>
                      <div className="font-medium">Vídeo</div>
                      <div className="text-sm text-neutral-400">Câmera e dispositivos de vídeo</div>
                    </div>
                  </div>
                </button>

                <button
                  className="w-full p-4 bg-neutral-800 rounded-lg text-left text-white hover:bg-neutral-700 transition-colors"
                  onClick={() => onViewChange('backgrounds')}
                >
                  <div className="flex items-center gap-3">
                    <ImageIcon className="w-6 h-6" />
                    <div>
                      <div className="font-medium">Fundos Virtuais</div>
                      <div className="text-sm text-neutral-400">Fundo da câmera e efeitos</div>
                    </div>
                  </div>
                </button>
              </div>
            )}

            {view === 'audio' && (
              <PreJoinSettingsMenu
                activeTab="audio"
                audioEnabled={audioEnabled}
                setAudioEnabled={setAudioEnabled}
                videoEnabled={videoEnabled}
                setVideoEnabled={setVideoEnabled}
                audioDeviceId={audioDeviceId}
                setAudioDeviceId={setAudioDeviceId}
                videoDeviceId={videoDeviceId}
                setVideoDeviceId={setVideoDeviceId}
                audioInputDevices={audioInputDevices}
                videoInputDevices={videoInputDevices}
                audioOutputDevices={audioOutputDevices}
                audioOutputDeviceId={audioOutputDeviceId}
                setAudioOutputDeviceId={setAudioOutputDeviceId}
                videoTrack={videoTrack}
                backgroundType={backgroundType}
                virtualBackgroundImagePath={virtualBackgroundImagePath}
                selectBackground={selectBackground}
                customBackgrounds={customBackgrounds}
                uploadCustomBackground={uploadCustomBackground}
                removeCustomBackground={removeCustomBackground}
                isUploadingCustomBackground={isUploadingCustomBackground}
                customBackgroundError={customBackgroundError}
                resetCustomBackgroundError={resetCustomBackgroundError}
                audioLevel={audioLevel}
                isAnalyzingAudio={isAnalyzingAudio}
                isPlayingTestSound={isPlayingTestSound}
                playTestSound={playTestSound}
                supportsSetSinkId={supportsSetSinkId}
              />
            )}

            {view === 'video' && (
              <PreJoinSettingsMenu
                activeTab="video"
                audioEnabled={audioEnabled}
                setAudioEnabled={setAudioEnabled}
                videoEnabled={videoEnabled}
                setVideoEnabled={setVideoEnabled}
                audioDeviceId={audioDeviceId}
                setAudioDeviceId={setAudioDeviceId}
                videoDeviceId={videoDeviceId}
                setVideoDeviceId={setVideoDeviceId}
                audioInputDevices={audioInputDevices}
                videoInputDevices={videoInputDevices}
                audioOutputDevices={audioOutputDevices}
                audioOutputDeviceId={audioOutputDeviceId}
                setAudioOutputDeviceId={setAudioOutputDeviceId}
                videoTrack={videoTrack}
                backgroundType={backgroundType}
                virtualBackgroundImagePath={virtualBackgroundImagePath}
                selectBackground={selectBackground}
                customBackgrounds={customBackgrounds}
                uploadCustomBackground={uploadCustomBackground}
                removeCustomBackground={removeCustomBackground}
                isUploadingCustomBackground={isUploadingCustomBackground}
                customBackgroundError={customBackgroundError}
                resetCustomBackgroundError={resetCustomBackgroundError}
                audioLevel={audioLevel}
                isAnalyzingAudio={isAnalyzingAudio}
                isPlayingTestSound={isPlayingTestSound}
                playTestSound={playTestSound}
                supportsSetSinkId={supportsSetSinkId}
              />
            )}

            {view === 'backgrounds' && (
              <PreJoinSettingsMenu
                activeTab="backgrounds"
                audioEnabled={audioEnabled}
                setAudioEnabled={setAudioEnabled}
                videoEnabled={videoEnabled}
                setVideoEnabled={setVideoEnabled}
                audioDeviceId={audioDeviceId}
                setAudioDeviceId={setAudioDeviceId}
                videoDeviceId={videoDeviceId}
                setVideoDeviceId={setVideoDeviceId}
                audioInputDevices={audioInputDevices}
                videoInputDevices={videoInputDevices}
                audioOutputDevices={audioOutputDevices}
                audioOutputDeviceId={audioOutputDeviceId}
                setAudioOutputDeviceId={setAudioOutputDeviceId}
                videoTrack={videoTrack}
                backgroundType={backgroundType}
                virtualBackgroundImagePath={virtualBackgroundImagePath}
                selectBackground={selectBackground}
                customBackgrounds={customBackgrounds}
                uploadCustomBackground={uploadCustomBackground}
                removeCustomBackground={removeCustomBackground}
                isUploadingCustomBackground={isUploadingCustomBackground}
                customBackgroundError={customBackgroundError}
                resetCustomBackgroundError={resetCustomBackgroundError}
                audioLevel={audioLevel}
                isAnalyzingAudio={isAnalyzingAudio}
                isPlayingTestSound={isPlayingTestSound}
                playTestSound={playTestSound}
                supportsSetSinkId={supportsSetSinkId}
              />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
